import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { useLang, useT } from "@/context/LanguageContext";

export function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const { lang, toggle } = useLang();
  const t = useT();
  const isHome = location.pathname === "/";
  const solid = scrolled || !isHome || open;

  const NAV = [
    { to: "/", label: t.nav.home },
    { to: "/proprietaires", label: t.nav.owners },
    { to: "/collection", label: t.nav.collection },
    { to: "/a-propos", label: t.nav.about },
    { to: "/contact", label: t.nav.contact },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  const LangSwitch = ({ dark }) => (
    <button onClick={toggle} data-testid="lang-toggle" className={`overline transition-opacity ${dark ? "text-ink" : ""}`}>
      <span className={lang === "fr" ? "" : "opacity-40"}>FR</span>
      <span className="opacity-40 mx-1">/</span>
      <span className={lang === "en" ? "" : "opacity-40"}>EN</span>
    </button>
  );

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 w-full z-50 transition-colors duration-500 ${
        solid ? "bg-ivory/95 backdrop-blur-md text-ink border-b border-ink/10" : "bg-black/10 backdrop-blur-[2px] text-ivory"
      }`}
    >
      <div className="max-w-[1500px] mx-auto px-5 sm:px-8 h-14 sm:h-[68px] flex items-center justify-between">
        <Link to="/" data-testid="logo-link" className="font-serif text-lg sm:text-xl tracking-wide leading-none">
          RIVIERA HOST <span className="italic">Collection</span>
        </Link>

        <nav className="hidden lg:flex items-center gap-9">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} data-testid={`nav-${n.to}`}
              className={({ isActive }) => `overline transition-opacity hover:opacity-100 ${isActive ? "opacity-100" : "opacity-70"}`}>
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-4 sm:gap-5">
          <LangSwitch />
          <Link to="/confier-mon-bien" data-testid="header-cta"
            className={`hidden sm:inline-flex px-6 py-3 overline transition-colors duration-300 ${
              solid ? "bg-ink text-ivory hover:bg-gold hover:text-ink" : "border border-ivory/70 hover:bg-ivory hover:text-ink"
            }`}>
            {t.nav.cta}
          </Link>
          <button data-testid="mobile-menu-toggle" className="lg:hidden" onClick={() => setOpen((o) => !o)} aria-label="Menu">
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav data-testid="mobile-nav" initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="lg:hidden overflow-hidden bg-ivory text-ink border-t border-ink/10">
            <div className="px-5 py-6 flex flex-col gap-1">
              {NAV.map((n) => (
                <NavLink key={n.to} to={n.to} className="font-serif text-3xl py-2" data-testid={`mobile-nav-${n.to}`}>{n.label}</NavLink>
              ))}
              <Link to="/confier-mon-bien" className="mt-4 bg-ink text-ivory text-center py-4 overline" data-testid="mobile-cta">{t.nav.cta}</Link>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
